import { BasePublisher, JapanesePublisherLocalization } from '@oneappexchange/oaf-schema';
import { AwardCard } from 'listing/awardDeck';
import { ListingCardProps } from 'wds/listingCard';
import { APPX_SITE_URL } from '~/modules/config';
import { BadgeKey } from '~/modules/helpers/badgeHelper';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listing_service';
import { i18nStore } from '~/modules/stores/i18nStore';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import {
    getListingPlatformType,
    getSlackSlug,
    isConsultantListing,
    selectExtensionListing,
} from '~/modules/utilities/listing_utils';
import { isSlackListingsEnabled } from '~/modules/utilities/slackPilot';
import { logger } from '../utilities/logger';

/**
 *
 * @param listing
 * @returns  array with social impact features labels to map from localization file based on direct oAF field mappings
 */
export function selectSocialImpactFeatures(listingResponse: ListingResponse) {
    const listing = selectExtensionListing(listingResponse);

    const socialImpactFeatures: BadgeKey[] = [];

    // Add supported features badge text from mapping
    if (listing?.isPledge1Percent) {
        socialImpactFeatures.push('isPledge1Percent');
    }
    if (listing?.isDiverseOwnedBusiness) {
        socialImpactFeatures.push('isDiverseOwnedBusiness');
    }

    return socialImpactFeatures;
}

/** Keys of the provider achievements localization block */
export type ProviderAchievementKey = keyof I18nMessages['listingDetails']['achievements'];

/** Badge image served from `public/` for each provider achievement. */
export const ACHIEVEMENT_IMAGE_URLS: Record<ProviderAchievementKey, string> = {
    partnerOfYear: '/images/listing/poty-partner.png',
    summitTier: '/images/listing/summit-partner.png',
    fdePartner: '/images/listing/fde-partner.png',
};

/**
 * Partner of the Year badge art keyed by award cycle year — each cycle has its
 * own artwork. A cycle whose asset has not been published yet is intentionally
 * absent here: {@link ACHIEVEMENT_IMAGE_URLS.partnerOfYear} covers the aggregate
 * deck badge, while the per-award achievement cards render no image (blank) for
 * an unmapped year until its art is added. Add future years (2028, 2029, …) as
 * their assets land.
 */
export const POTY_IMAGE_BY_CYCLE_YEAR: Record<number, string> = {
    2027: '/images/listing/poty-partner.png',
};

/**
 * Provider achievements a listing has earned. Partner of the Year ranks first
 * (spike doc) when `includePartnerOfYear` is true and the publisher has any
 * `publisherAwards` entry (current or past); it is gated behind `FF_TRYBUY_POTY_BADGE`
 * by the caller. Summit-tier partners and Forward Deployed Engineering partners
 * follow, read from a consultant listing's competencies. A listing with none of
 * these shows no section.
 *
 * The Summit-tier and FDE-partner achievements are gated on the listing being a
 * consultant listing: a non-consultant listing yields neither, regardless of any
 * `consultantCompetencies` on the response. A missing `isFDEPartner` is treated as
 * `false`.
 *
 * @param listingResponse the resolved listing
 * @param includePartnerOfYear whether to consider the Partner of the Year achievement
 * @returns the achievement keys to render, in display order (empty when none apply)
 */
export function selectProviderAchievements(
    listingResponse?: ListingResponse | null,
    includePartnerOfYear = false,
): ProviderAchievementKey[] {
    const achievements: ProviderAchievementKey[] = [];

    // Partner of the Year ranks first (spike doc). Publisher-level, all listing
    // types: present when the publisher has any award (current or past). Gated by
    // the caller via FF_TRYBUY_POTY_BADGE.
    if (includePartnerOfYear) {
        const hasAward = (listingResponse?.publisherAwards?.awards?.length ?? 0) > 0;
        if (hasAward) {
            achievements.push('partnerOfYear');
        }
    }

    // Summit / FDE are consultant-listing achievements only (a non-consultant
    // listing yields neither, regardless of any consultantCompetencies present).
    const competencies = listingResponse?.consultantCompetencies;
    const isConsultant = isConsultantListing(listingResponse ?? undefined);
    if (isConsultant && competencies?.tier === 'Summit') {
        achievements.push('summitTier');
    }
    if (isConsultant && competencies?.isFDEPartner === true) {
        achievements.push('fdePartner');
    }

    return achievements;
}

/**
 * Award-deck cards for a listing's provider achievements: one card per key from
 * {@link selectProviderAchievements}, pairing the badge image with the
 * achievement title as accessible alt text. Empty when the listing has none.
 *
 * @param listingResponse the resolved listing
 * @param includePartnerOfYear whether to consider the Partner of the Year achievement
 * @returns the cards to feed `<listing-award-deck>`, in display order
 */
export function selectProviderAchievementCards(
    listingResponse?: ListingResponse | null,
    includePartnerOfYear = false,
): AwardCard[] {
    const achievements = i18nStore.getState().i18nStaticCache.listingDetails?.achievements;
    // When the locale lacks the achievements localization block (e.g. JP/ES), the
    // achievements section renders nothing, so emit no deck cards either. Otherwise
    // the header deck would be a clickable jump to an empty section (WCAG 2.4.4 —
    // the target the deck scrolls to must exist).
    if (!achievements) {
        return [];
    }

    return selectProviderAchievements(listingResponse, includePartnerOfYear).map((key) => ({
        id: key,
        src: ACHIEVEMENT_IMAGE_URLS[key],
        alt: achievements[key]?.title ?? '',
    }));
}

export function getListingPublisher(
    listing?: ListingResponse | null,
): BasePublisher | JapanesePublisherLocalization | null {
    if (getUserLanguage() === JAPANESE_LANGUAGE_CODE) {
        return listing?.publisher?.marketplaceLocalization?.jp ?? listing?.publisher ?? null;
    }
    return listing?.publisher ?? null;
}

export function getListingLogo(listing?: ListingResponse | null): string {
    const listingWithLogos = listing as ListingResponse & { 'listing/plugins/LogoSet': { Logo?: string } };
    const logo = generateMediaUrl(listingWithLogos?.['listing/plugins/LogoSet']?.Logo ?? '');
    if (!logo) {
        logger.error(`Failed to find a Logo for listing ${listing!.id}`);
    }
    return logo;
}

export function getCategoryTagsForTiles(listing: ListingResponse): AnyObject[] {
    return selectExtensionListing(listing)?.listingCategories?.map((name) => ({ name })) ?? [];
}

/**
 * Maps a resolved ListingResponse to the card shape `wds-card-grid`/`wds-listing-card` expect.
 * Shared by the curated and personalized listing MFEs so the card mapping lives in one place.
 * `pricing` is intentionally omitted — these surfaces don't render a price.
 */
export function listingToCardData(listing: ListingResponse): Omit<ListingCardProps, 'pricing'> {
    // The platform badge reflects the listing's identity (see getListingPlatformType), so a slack
    // tile shows the Slack icon regardless of the pilot. The marketplace slug — which drives the
    // outbound Slack link — is gated on the pilot: with the flag off (or no slug) the tile keeps
    // the internal listing link, matching search/explore. (W-24063286)
    const slackSlug = isSlackListingsEnabled() ? getSlackSlug(listing) : undefined;
    return {
        external_id: listing.external_id || '',
        oafId: listing.external_id || '',
        tzId: listing.tzId || '',
        appExchangeId: listing.appExchangeId || '',
        listingUrl: `${APPX_SITE_URL}/${Routes.LISTING}?listingId=${listing.external_id}`,
        averageRating: listing.reviewsSummary?.averageRating ?? 0,
        reviewsAmount: listing.reviewsSummary?.reviewCount ?? 0,
        publisher: listing.publisher?.name ?? '',
        listingCategories: getCategoryTagsForTiles(listing) as { name: string; filter?: string }[],
        image: getListingLogo(listing),
        title: listing.name,
        description: listing.description,
        type: selectExtensionListing(listing).recordType || '',
        chatEnabled: false,
        sponsored: false,
        // Drives the platform badge icon in the `with-badge` card variant. Without it the card
        // falls back to the Salesforce icon for every listing, including consultants (W-24063286).
        platformType: getListingPlatformType(listing),
        // Slack tiles need the slug to link out to the Slack marketplace (icon is driven by
        // platformType, not the slug).
        ...(slackSlug ? { additionalProps: { slackSlug } } : {}),
    };
}
